import { WizBaseComponent, WizIcon, WizPopUp, WizTextInput, WizValidationError, WizValidationErrors, WizValidatorFn } from '@wizeoo/akm-component-lib';
import { BusinessException, EntityModificationPack } from '@wizeoo/akm-message-lib';
import { WIZ_COMMAND_STATE } from '@wizeoo/akm-core-lib';
import { cloneDeep } from 'lodash-es';
import { EM_ACTION } from '@wizeoo/akm-compute-lib';

import { CountryVO } from '../../vo/locale/country.vo';
import { Model } from '../../model/model';
import { BusinessUnitVO } from '../../vo/core/business-unit.vo';
import { LanguageVO } from '../../vo/locale/language.vo';
import { EMEngine } from '../entities/entity-modification.engine';
import { CommitCmd } from '../../command/em/commit.cmd';
import { ViewData } from '../../model/view.data';


/**
 * Manager de langues.
 */
export class LanguageEngine {

  protected _model: Model = null;
  constructor(model: Model) {
    this._model = model;
  }
  public get model(): Model {
    return this._model as Model;
  }

  /** alimentation du clone de travail sur la base du currentEdited */
  public getWorkingClone(): LanguageVO {
    let result: LanguageVO = null;
    if (this.model.currentEditedLanguage) {
      result = new LanguageVO(this.model, cloneDeep(this.model.currentEditedLanguage.source));
    } else {
      result = null;
    }
    return result;
  }

  /** Function to creation language, réservé domain et bu '*' */
  public createObjectLanguage(): LanguageVO {
    const language = new LanguageVO(this.model);
    language.domain = ViewData.wildcardDomain;
    language.businessUnitCode = ViewData.wildcardDomain;
    return language;
  }

  /**
   * récupération de la liste des pays possédant la langue passée en paramètre
   * @param source : LanguageVO la langue concernée
   * @return  CountryVO[], return un liste de pays utilisant la langue renseignée
   */
  public getLanguageCountries(language: LanguageVO): CountryVO[] {
    if (this.model.countryMap) {
      return Array.from(this.model.countryMap.values()).filter((country: CountryVO) => this.model.countryEngine.hasLanguage(country, language));
    }
    return [];
  }

  /**
   * récupération de la liste des bu possédant la langue passée en paramètre
   * @param source : LanguageVO la langue concernée
   * @return BusinessUnitVO[], return un liste des bu utilisant la langue renseignée
   */
  public getLanguageBussinessUnits(language: LanguageVO): BusinessUnitVO[] {
    // LMA inutile
    // if (Model.instance.businessUnitList) {
    //   return Model.instance.businessUnitList.filter(bu => this.model.businessUnitEngine.hasLanguage(bu, language));
    // }
    return [];
  }

  /**
   * Retourne la même instance de language que dans la liste du modèle.
   * @param source : LanguageVO la langue concernée
   * @return LanguageVO, return l'instance de langue renseignée
   */
  public getCurrentInstance(source: LanguageVO): LanguageVO {
    let result: LanguageVO = null;
    if (source && this.model.languageMap) {
      result = this.model.languageMap.get(source.code);
    }
    return result;
  }

  /**
   * Véification que l'object language est valid (code, businessUnitCode, domain, label et alphabet)
   * @param language la langue à valider
   * @return boolean true si la langue est valide, false sinon
   */
  public isValid(language: LanguageVO): boolean {
    if (!language) {
      return false;
    }

    if (language.code === undefined || language.code === null) {
      return false;
    }
    if (language.domain === undefined || language.domain === null) {
      return false;
    }
    if (language.businessUnitCode === undefined || language.businessUnitCode === null) {
      return false;
    }
    if (language.label === undefined || language.label === null) {
      return false;
    }

    if (language.alphabet === undefined || language.alphabet === null) {
      return false;
    }
    return true;
  }


  /**
   * Validateur d'unicité du code
   * @param currentLanguage: object LanguageVO représentant la langue en cours de création (permet de ne pas gérer l'erreur s'il est null)
   */
  public uniqueCodeValidator(lang: LanguageVO, composant: WizTextInput): WizValidatorFn {
    return (compo: WizBaseComponent): WizValidationErrors | null => {

      // Pas de gestion d'erreur si le currentLanguage est null
      if (lang === null || lang === undefined) {
        return null;
      }

      if (composant.getWizValue() !== null && composant.getWizValue() && composant.getWizValue().length > 0 && !lang.exist) {
        // get language dans le model
        const chrono = this.model.languageMap.get(composant.getWizValue().toLowerCase());

        // Si un language existe deja avec le même code on affiche le message
        if (chrono) {
          const temp: WizValidationError = {
            wizError: '$.language.error.code.unique.$',
            wizErrorParams: []
          };
          return [temp];
        }
      }
      return null;
    };
  }

  /**
   * recherche la langue par défaut, soit le contexte utilisateur, soit la BU en cours
   * @return LanguageVO
   */
  public getDefaultLanguage(): LanguageVO {
    let result: LanguageVO = null;

    // recherche d'une entrée de contexte utilisateur (key = code country)
    // const contextLanguage: UserContextVO = ContextEngine.getContext(CONTEXT_RANGE.CONTEXT_TAB, CONTEXT.SELECTED_ITEM);
    // if (contextLanguage) {
    //   result = this.model.languageMap.get(contextLanguage.valor);
    // }

    if (!result) { // pas trouvé, context obsolète
      if (this.model.currentEditedLanguage && this.model.currentEditedLanguage.code !== undefined) { // dernière langue éditée sauf si la dernière était en cours de création...
        result = this.getCurrentInstance(this.model.currentEditedLanguage);
      } else { // BU par defaut
        let bu: BusinessUnitVO;
        bu = this.model.businessUnitEngine.getByBusinessUnitCode(this.model.domainCode, this.model.businessUnitCode);
        if (bu) {
          result = bu.defaultLanguagesVO;
        }
      }
    }
    return result;
  }

  /**
   * recherche la langue par defaut du contexte de l'application (celle de la BU)
   * @return LanguageVO
   */
  public getDefaultContextLanguage(): LanguageVO {
    let result: LanguageVO = null;
    // BU par defaut
    const bu: BusinessUnitVO = this.model.businessUnitEngine.getDefaultContextBU();
    if (bu) {
      result = bu.defaultLanguagesVO;
    }
    return result;
  }

  /**
   * Retourne la langue de la liste par son code.
   * @param code : BusinessUnitVO.businessUnitCode cherché
   * @return LanguageVO la langue récupéré en base ou null si elle n'existe pas
   */
  public getLanguageByCode(code: string): LanguageVO {
    let result: LanguageVO = null;
    if (code && this.model.languageList) {
      const tmpColl = this.model.languageList.filter((item: LanguageVO) => item.code === code);
      if (tmpColl.length === 1) {
        result = tmpColl[0];
      }
    }
    return result;
  }

  /**
   * La langue passée en paramètre est-elle utilisée par un pays ou une bu
   * @param language La langue
   * @return boolean Le résultat du test
   */
  public isUsedLanguage(language: LanguageVO): boolean {
    const currentLanguage: LanguageVO = this.getCurrentInstance(language);
    const countryList: CountryVO[] = this.getLanguageCountries(currentLanguage);
    if (countryList && countryList.length > 0) {
      return true;
    }
    const buList: BusinessUnitVO[] = this.getLanguageBussinessUnits(currentLanguage);
    if (buList && buList.length > 0) {
      return true;
    }
    return false;
  }

  /**
   * Recherche des langues pour une BU donnée
   * @param domain Le domaine concerné (wildcard ou code BU)
   */
  public getLanguageListForBU(domain: string): LanguageVO[] {
    let languageList: LanguageVO[] = [];
    if (domain !== ViewData.wildcardDomain) {
      const bu: BusinessUnitVO = this.model.businessUnitEngine.getByBusinessUnitCode(this.model.domainCode, domain);
      if (bu) {
        languageList = bu.languagesVO;
      }
    }
    // si la bu n'a aucune langue ou que le domaine est wildcardDomain
    if (languageList.length === 0) {
      languageList = this.model.languageList;
    }
    const result: LanguageVO[] = [];
    if (languageList && languageList.length > 0) {
      languageList.forEach(vo => {
        if (!vo.deleted && !vo.deprecated) {
          result.push(vo);
        }
      });
    }
    return result;
  }

  /**
   * create/ update d'une langue
   * @param to le clone en cours de modif
   * @param from l'objet d'origine, inaltéré
   * @param callback retour dans la vue
   */
  public merge(to: LanguageVO, from: LanguageVO, callback: Function): void {
    let pack: EntityModificationPack;
    if (to.exist) {
      pack = EMEngine.mergeEM(to, from);
    } else {
      pack = EMEngine.createEM(to);
    }
    if (pack) { // a minima une modif, sinon, c'est le même objet (update)
      new CommitCmd(this.model).commit(pack, this.backFromCall, [to], callback).execute();
    } else {
      // modèle, le clone est une copie de la valeur d'origine
      ViewData.wizUpdateModel = false;
      callback(to, WIZ_COMMAND_STATE.SUCCESS); // retour en vue
    }
  }

  /**
   * delete d'une LanguageVO
   * @param to le clone à supprimer
   * @param callback retour dans la vue
   */
  public remove(to: LanguageVO, callback: Function): void {
    const pack: EntityModificationPack = EMEngine.removeEM(to);
    new CommitCmd(this.model).commit(pack, this.backFromCall, [to], callback).execute();
  }

  /**
   * gestion unifiée des retours de service CUD
   * @param cmd la commande commit qui porte les EMs et les objets
   */
  private backFromCall(cmd: CommitCmd): void {
    // modèle OK (rollback en cas d'erreur)
    ViewData.wizUpdateModel = false;

    // erreur physique
    if (cmd.state === WIZ_COMMAND_STATE.FAULT) {
      WizPopUp.toast('$.common.toast.service.error.$', WizIcon.TOAST_ERROR).show();
    } else {
      // le VO source
      const vo: any = cmd.sourceVO[0];
      // une seule modification à la fois, packEntry donne l'objet
      const source: any = cmd.result.entries[0].entity;
      // l'action (un seul type)
      const operation: string = cmd.result.ems[0].operation;
      // la liste d'erreurs métier
      const errors: BusinessException[] = cmd.result.errors;
      // le statut du pack
      const packStatus: string = cmd.result.status;

      if (packStatus === WIZ_COMMAND_STATE.FAULT) {
        errors.forEach((be: BusinessException) => {
          WizPopUp.toast(be.message, WizIcon.TOAST_ERROR).show();
        });

      } else {
        switch (operation) {
          case EM_ACTION.UPDATE:
            vo.source = source; // le clone
            const modelLanguage: LanguageVO = this.model.languageMap.get(vo.code);
            if (modelLanguage) {
              modelLanguage.source = source;
            }
            break;
          case EM_ACTION.CREATE:
            vo.source = source;
            this.model.languageMap.set(vo.code, vo);
            this.model.languageList.push(vo);
            break;
          case EM_ACTION.DELETE:
            vo.source = source;
            this.model.languageMap.delete(vo.code);
            this.model.languageList.splice(this.model.languageList.indexOf(vo), 1);
        }
      }
      cmd.viewCallBack(vo, packStatus); // retour en vue
    }
  }
}
